import React, { useEffect, useState, useCallback } from "react";
import MasterSideBar from "../../components/MasterSideBar";
import Topbar from "../../components/TopbarNav";
import LeaveRequestForm from "../../components/LeaveRequestForm";
import PremiumHeader from "../../components/common/PremiumHeader";
import PremiumStatCard from "../../components/common/PremiumStatCard";
import PremiumCard from "../../components/common/PremiumCard";
import { jwtDecode } from "jwt-decode";
import { 
  Plus, AlertTriangle, Package, FileText, 
  Truck, Activity, X, PieChart as PieChartIcon, 
  BarChart3, TrendingUp 
} from "lucide-react";
import ReactECharts from "echarts-for-react";
import { getVendorStats } from "../../api/vendorApi";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { motion, AnimatePresence } from "framer-motion";
import { ToastContainer } from "react-toastify";

export default function OADashboard() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showLeaveForm, setShowLeaveForm] = useState(false);

  // Stats States
  const [requisitionStats, setRequisitionStats] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [projectStats, setProjectStats] = useState({ pending: 0, total: 0 });
  const [vendorStats, setVendorStats] = useState({ active: 0, pending: 0, suspended: 0 });
  const [stockoutCount, setStockoutCount] = useState(0);
  const [lowStockCount, setLowStockCount] = useState(0);
  const [items, setItems] = useState([]);
  const [expiringContractsCount, setExpiringContractsCount] = useState(0);
  const [unpaidInvoicesTotal, setUnpaidInvoicesTotal] = useState(0);

  const token = localStorage.getItem("token");
  const decoded = token ? jwtDecode(token) : null;
  const staffId = decoded ? (decoded.id || decoded.userId) : null;

  const fetchStats = useCallback(async () => {
    setIsLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };

      // 1. Requisitions
      const reqRes = await fetch("https://ims.holdthechild.net/api/requisitions", { headers });
      const reqData = await reqRes.json();
      const rList = Array.isArray(reqData) ? reqData : (reqData.data || []);
      setRequisitionStats(rList.reduce((acc, req) => {
        const status = req.directorApproval?.status;
        if (status === "pending") acc.pending++;
        else if (status === "approved") acc.approved++;
        else if (status === "rejected") acc.rejected++;
        return acc;
      }, { pending: 0, approved: 0, rejected: 0 }));

      // 2. Assets
      const assetsRes = await fetch("https://ims.holdthechild.net/api/assets", { headers });
      const assetsDataRaw = await assetsRes.json();
      const aList = Array.isArray(assetsDataRaw) ? assetsDataRaw : (assetsDataRaw.data || []);
      setProjectStats({ pending: aList.filter(a => a.status === 'Needs Review').length, total: aList.length });

      // 3. Vendors & Contracts
      const vRes = await getVendorStats();
      const vData = vRes.data?.data || {};
      setVendorStats({ active: vData.active || 0, pending: vData.pending || 0, suspended: vData.suspended || 0 });

      // 4. Inventory
      const invRes = await fetch("https://ims.holdthechild.net/api/inventory", { headers });
      const invData = await invRes.json();
      const iList = Array.isArray(invData) ? invData : (invData.data || []);
      setItems(iList);
      setStockoutCount(iList.filter(i => i.inStock === 0).length);
      setLowStockCount(iList.filter(i => i.inStock <= (i.minStockLevel || 0)).length);

      // 5. Unpaid Invoices
      const invcRes = await fetch("https://ims.holdthechild.net/api/vendor-invoices", { headers });
      const invcData = await invcRes.json();
      const invoiceList = Array.isArray(invcData) ? invcData : (invcData.data || []);
      setUnpaidInvoicesTotal(invoiceList.filter(i => i.status?.toLowerCase() !== "paid").reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0));

    } catch (err) {
      console.error("Dashboard Fetch Error:", err);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => { if (token) fetchStats(); }, [fetchStats, token]);

  const inventoryChartData = Object.values(
    items.reduce((acc, item) => {
      const cat = item.category || "Uncategorized";
      if (!acc[cat]) acc[cat] = { name: cat, stock: 0 };
      acc[cat].stock += item.inStock || 0;
      return acc;
    }, {})
  ).slice(0, 8); // Limit to top 8 for UI clarity

  return (
    <div className="flex min-h-screen bg-gray-50 font-sans overflow-hidden">
      <MasterSideBar
        visible={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        collapsed={sidebarCollapsed}
        setCollapsed={setSidebarCollapsed}
      />

      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0 md:ml-64'}`}>
        <Topbar onSidebarToggle={() => setSidebarOpen(true)} />

        <main className="flex-1 overflow-y-auto p-4 md:p-8 mt-16 lg:mt-20">
          <div className="max-w-7xl mx-auto">
            
            <div className="flex flex-col items-center text-center mb-12">
              <PremiumHeader 
                title="Operations" 
                lastWordAccent="Command" 
                subtitle="Logistics, Procurement & Asset Lifecycle Oversight." 
              />
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowLeaveForm(true)}
                className="mt-8 flex items-center gap-3 bg-gray-900 text-white px-10 py-4 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] hover:bg-orange-600 shadow-2xl shadow-gray-200 transition-all active:scale-95"
              >
                <Plus size={16} strokeWidth={3} /> Submit Absence Request
              </motion.button>
            </div>

            {isLoading ? (
              <div className="flex flex-col items-center justify-center h-80">
                <div className="w-12 h-12 border-4 border-orange-600 border-t-transparent rounded-full animate-spin mb-4" />
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Aggregating Global Stats...</p>
              </div>
            ) : (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-12"
              >
                {/* KPI Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  <PremiumStatCard 
                    title="Req. Pending" 
                    value={requisitionStats.pending} 
                    icon={<FileText className="text-orange-600" />} 
                    iconBg="bg-orange-50" 
                    description="Awaiting Director Review" 
                  />
                  <PremiumStatCard 
                    title="Asset Reviews" 
                    value={projectStats.pending} 
                    icon={<Activity className="text-amber-600" />} 
                    iconBg="bg-amber-50" 
                    description="Maintenance flagged" 
                  />
                  <PremiumStatCard 
                    title="Active Vendors" 
                    value={vendorStats.active} 
                    icon={<Truck className="text-emerald-600" />} 
                    iconBg="bg-emerald-50" 
                    description="Verified partners" 
                  />
                  <PremiumStatCard 
                    title="Vendor Queue" 
                    value={vendorStats.pending} 
                    icon={<Package className="text-blue-600" />} 
                    iconBg="bg-blue-50" 
                    description="Compliance checks" 
                  />
                </div>

                {/* Alerts Section */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="group bg-white border border-red-100 rounded-[2.5rem] p-8 hover:shadow-xl hover:shadow-red-50/50 transition-all duration-500 relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                      <AlertTriangle size={80} className="text-red-500" />
                    </div>
                    <div className="flex items-center gap-4 mb-6 relative">
                      <div className="size-12 rounded-2xl bg-red-50 text-red-500 flex items-center justify-center">
                        <AlertTriangle size={24} />
                      </div>
                      <h3 className="font-black text-[10px] uppercase tracking-[0.2em] text-red-900/60">Inventory Critical</h3>
                    </div>
                    <div className="space-y-2 relative">
                      <p className="text-3xl font-black text-gray-800">{stockoutCount}</p>
                      <p className="text-xs font-bold text-red-600 underline decoration-2 underline-offset-4">Items at zero stock level</p>
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-4">Threshold Breached: {lowStockCount} items</p>
                    </div>
                  </div>

                  <div className="group bg-white border border-amber-100 rounded-[2.5rem] p-8 hover:shadow-xl hover:shadow-amber-50/50 transition-all duration-500 relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                      <FileText size={80} className="text-amber-500" />
                    </div>
                    <div className="flex items-center gap-4 mb-6 relative">
                      <div className="size-12 rounded-2xl bg-amber-50 text-amber-500 flex items-center justify-center">
                        <FileText size={24} />
                      </div>
                      <h3 className="font-black text-[10px] uppercase tracking-[0.2em] text-amber-900/60">Contract Expiry</h3>
                    </div>
                    <div className="space-y-2 relative">
                      <p className="text-3xl font-black text-gray-800">{expiringContractsCount}</p>
                      <p className="text-xs font-bold text-amber-600 underline decoration-2 underline-offset-4">Renewals due within 30 days</p>
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-4">Immediate Review Required</p>
                    </div>
                  </div>

                  <div className="group bg-white border border-orange-100 rounded-[2.5rem] p-8 hover:shadow-xl hover:shadow-orange-50/50 transition-all duration-500 relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                      <TrendingUp size={80} className="text-orange-500" />
                    </div>
                    <div className="flex items-center gap-4 mb-6 relative">
                      <div className="size-12 rounded-2xl bg-orange-50 text-orange-500 flex items-center justify-center">
                        <TrendingUp size={24} />
                      </div>
                      <h3 className="font-black text-[10px] uppercase tracking-[0.2em] text-orange-900/60">Payable Balance</h3>
                    </div>
                    <div className="space-y-2 relative">
                      <p className="text-3xl font-black text-gray-800">${unpaidInvoicesTotal.toLocaleString()}</p>
                      <p className="text-xs font-bold text-orange-600 underline decoration-2 underline-offset-4">Pending clearance totals</p>
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-4">Across all verified vendors</p>
                    </div>
                  </div>
                </div>

                {/* Charts Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <PremiumCard title="Requisition Status Breakdown" icon={<PieChartIcon size={18} />}>
                    <div className="py-6">
                      <ReactECharts
                        option={{
                          tooltip: { trigger: "item" },
                          legend: { bottom: 0, left: 'center', icon: 'circle', textStyle: { fontSize: 10, fontWeight: 'bold' } },
                          series: [{
                            type: "pie",
                            radius: ["45%", "70%"],
                            avoidLabelOverlap: false,
                            itemStyle: { borderRadius: 12, borderColor: '#fff', borderWidth: 4 },
                            label: { show: false },
                            data: [
                              { value: requisitionStats.pending, name: "Pending", itemStyle: { color: "#f97316" } },
                              { value: requisitionStats.approved, name: "Approved", itemStyle: { color: "#10b981" } },
                              { value: requisitionStats.rejected, name: "Rejected", itemStyle: { color: "#ef4444" } },
                            ]
                          }]
                        }}
                        style={{ height: "320px" }}
                      />
                    </div>
                  </PremiumCard>

                  <PremiumCard title="Inventory Distribution" icon={<BarChart3 size={18} />}>
                    <div className="py-6 px-4">
                      <ResponsiveContainer width="100%" height={320}>
                        <BarChart data={inventoryChartData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                          <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 'black', fill: '#94a3b8', textTransform: 'uppercase' }} />
                          <YAxis hide />
                          <Tooltip 
                            cursor={{ fill: '#f8fafc' }} 
                            contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', padding: '12px' }} 
                          />
                          <Bar dataKey="stock" radius={[10, 10, 10, 10]} barSize={24}>
                            {inventoryChartData.map((_, i) => <Cell key={i} fill={["#f97316", "#fb923c", "#fdba74", "#ffedd5"][i % 4]} />)}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </PremiumCard>
                </div>
              </motion.div>
            )}
          </div>
        </main>
      </div>

      <AnimatePresence>
        {showLeaveForm && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              exit={{ opacity: 0 }} 
              onClick={() => setShowLeaveForm(false)} 
              className="absolute inset-0 bg-black/40 backdrop-blur-sm" 
            />
            <motion.div 
              initial={{ scale: 0.95, opacity: 0, y: 20 }} 
              animate={{ scale: 1, opacity: 1, y: 0 }} 
              exit={{ scale: 0.95, opacity: 0, y: 20 }} 
              className="relative bg-white rounded-[2.5rem] shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-gray-100"
            >
              <div className="px-10 py-8 border-b border-gray-50 flex items-center justify-between bg-gray-50/50">
                <div className="flex items-center gap-4">
                  <div className="size-10 rounded-2xl bg-orange-50 text-orange-600 flex items-center justify-center">
                    <Plus size={20} strokeWidth={3} />
                  </div>
                  <h2 className="text-xs font-black text-gray-800 uppercase tracking-widest">Submit Absence Request</h2>
                </div>
                <button 
                  onClick={() => setShowLeaveForm(false)} 
                  className="p-3 bg-white hover:bg-gray-100 rounded-2xl transition-all active:scale-95 border border-gray-100 shadow-sm"
                >
                  <X size={20} className="text-gray-400" />
                </button>
              </div>
              <div className="p-10 overflow-y-auto max-h-[calc(90vh-120px)]">
                {staffId && <LeaveRequestForm staffId={staffId} token={token} onClose={() => setShowLeaveForm(false)} />}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <ToastContainer position="bottom-right" theme="dark" />
    </div>
  );
}
